import { hasMeaningfulOnboardingValue } from '@/lib/ai/onboardingFieldValues'
import { meetsAA } from '@/lib/colorContrastRatio.js'

import type { OnboardingFormData } from './onboardingPrefill'

export const BRAND_COLOR_FIELDS = ['primaryColor', 'secondaryColor'] as const
export type BrandColorField = (typeof BRAND_COLOR_FIELDS)[number]

type BrandColors = Pick<OnboardingFormData, BrandColorField>

// Stricter than parseHexToRgba on purpose: no alpha. A semi-transparent brand color is only
// readable relative to whatever it ends up on, and the widget background isn't guaranteed white.
// This check must also run first, because parseHexToRgba turns invalid input into black,
// which would pass any contrast check against white.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

// The guide/checklist button renders a white label on these colors.
const BUTTON_LABEL_COLOR = '#FFFFFF'

const CONTRAST_MESSAGE_KEYS: Record<BrandColorField, string> = {
  primaryColor: 'onboarding.wizard.data-entry.contrast.primary-too-light',
  secondaryColor: 'onboarding.wizard.data-entry.contrast.secondary-too-light',
}

export const isBrandColorField = (field: string): field is BrandColorField =>
  (BRAND_COLOR_FIELDS as readonly string[]).includes(field)

/**
 * Returns an i18n key if `value` must not be stored for `field`, otherwise null.
 * Empty is allowed: it means "not set" and the default theme applies.
 */
export function getBrandColorIssue(
  field: BrandColorField,
  value: string | null | undefined
): string | null {
  const candidate = (value ?? '').trim()
  if (!hasMeaningfulOnboardingValue(field, candidate)) return null
  if (!HEX_COLOR.test(candidate)) return 'onboarding.wizard.data-entry.color-invalid'

  // Same helper and default threshold as the existing warning, so the two can't disagree.
  return meetsAA(BUTTON_LABEL_COLOR, candidate) ? null : CONTRAST_MESSAGE_KEYS[field]
}

/** First stored brand color that fails, e.g. one written by AI capture or older data. */
export function getFirstBrandColorIssue(
  current: BrandColors
): { field: BrandColorField; issue: string } | null {
  for (const field of BRAND_COLOR_FIELDS) {
    const issue = getBrandColorIssue(field, current[field])
    if (issue) return { field, issue }
  }
  return null
}
